// approval.js
// Code execution always requires human approval before running — no
// autonomous execution, per the earlier design decision. This module is
// the state machine for that: the AI peer requests approval by sending a
// normal group message showing the EXACT code (not a paraphrase — a
// human approving a description of code isn't approving the code), waits
// for a human to approve or reject, and fails closed (treated as
// rejected) if nobody responds within the timeout.
//
// Message formats travel as JSON-tagged group messages, same convention
// as settings.js — application-layer, layered on top of group.js's plain
// text messages. A real UI would render the request as an actual
// approve/reject button pair that emits the response message
// automatically, rather than asking a human to type JSON by hand.

const REQUEST_TYPE = 'ai-peer-exec-request/v1'
const RESPONSE_TYPE = 'ai-peer-exec-response/v1'
const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000 // fail closed after 5 minutes of silence

let idCounter = 0
const nextId = () => `exec-${Date.now()}-${idCounter++}`

export function formatExecutionRequest (id, language, code) {
  return JSON.stringify({ type: REQUEST_TYPE, id, language, code, at: new Date().toISOString() })
}

export function formatExecutionResponse (requestId, approved, byFingerprint) {
  return JSON.stringify({ type: RESPONSE_TYPE, requestId, approved: !!approved, by: byFingerprint, at: new Date().toISOString() })
}

function parseTagged (messageText, expectedType) {
  let parsed
  try {
    parsed = JSON.parse(messageText)
  } catch {
    return null
  }
  return parsed?.type === expectedType ? parsed : null
}

export function parseExecutionRequest (messageText) {
  return parseTagged(messageText, REQUEST_TYPE)
}

export function parseExecutionResponse (messageText) {
  return parseTagged(messageText, RESPONSE_TYPE)
}

export class ApprovalManager {
  /**
   * @param {(groupId: string, messageText: string) => void} sendToGroup -
   *   called to actually broadcast the approval-request message
   * @param {number} [timeoutMs]
   */
  constructor (sendToGroup, timeoutMs = DEFAULT_TIMEOUT_MS) {
    this.sendToGroup = sendToGroup
    this.timeoutMs = timeoutMs
    this.pending = new Map() // requestId -> { resolve, groupId, timer }
  }

  /**
   * Request approval to run `code`. Resolves once a human responds, or
   * after the timeout (resolves with approved: false either way — this
   * never rejects, since "nobody approved" is a normal, expected outcome,
   * not an error condition).
   * @returns {Promise<{approved: boolean, by?: string, reason?: string}>}
   */
  requestExecution (groupId, language, code) {
    const id = nextId()
    this.sendToGroup(groupId, formatExecutionRequest(id, language, code))

    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.pending.delete(id)
        resolve({ approved: false, reason: 'timed out waiting for approval' })
      }, this.timeoutMs)

      this.pending.set(id, { resolve, groupId, timer })
    })
  }

  /**
   * Feed every incoming group message through this. Returns true if the
   * message was an approval/rejection response that got applied (so the
   * caller knows not to treat it as ordinary conversation), false
   * otherwise.
   */
  handleIncomingMessage (groupId, messageText, senderFingerprint) {
    const response = parseTagged(messageText, RESPONSE_TYPE)
    if (!response) return false

    const entry = this.pending.get(response.requestId)
    if (!entry || entry.groupId !== groupId) return false // stale, unknown, or wrong-group response

    clearTimeout(entry.timer)
    this.pending.delete(response.requestId)
    entry.resolve({ approved: response.approved, by: senderFingerprint })
    return true
  }

  /** How many executions are currently awaiting approval — mostly for tests/observability. */
  get pendingCount () {
    return this.pending.size
  }
}
